import { EditorView } from "prosemirror-view";
import type { EditorState } from "prosemirror-state";
import type { Msg } from "../types";
import { editorState } from "./cards";
import { liveNodeViews } from "./node-views";
import { renderCardPreview } from "./preview";
import { hideExtractButton, updateExtractButton } from "./extract";

type Dispatch = (msg: Msg) => void;

// The Model holds the EditorState of each card. A CardEditor is one EditorView
// that projects such a state onto the DOM, and it owns no document state. The
// main editor of a card and each section that shows the card are different
// projections of the same state.
const editors = new Set<CardEditor>();
// The main editor of each card. It stays after a navigation, so a return to the
// card keeps the DOM and the focus.
const mainEditors = new Map<string, CardEditor>();
// The editor that had the focus last. The toolbar changes this editor.
let focused: CardEditor | null = null;
const focusListeners = new Set<() => void>();

export class CardEditor {
  view: EditorView | null = null;

  // `path` holds the cards from the main card to this card. A section of a
  // card in the path shows only its title, because it would contain itself. A
  // collapsed section shows its editor read-only (`editable` false).
  constructor(
    readonly cardId: string,
    private el: HTMLElement,
    readonly path: string[],
    private dispatch: Dispatch,
    private editable = true
  ) {
    editors.add(this);
    this.sync();
  }

  // Make the view agree with the state in the published Model. The view starts
  // when the card loads, and it stops when the card is deleted.
  sync(): void {
    const state = editorState(this.cardId);
    if (!state) {
      this.view?.destroy();
      this.view = null;
      return;
    }
    if (!this.view) this.view = this.createView(state);
    else if (this.view.state !== state) this.view.updateState(state);
    if (focused === this) this.updateExtract();
  }

  destroy(): void {
    editors.delete(this);
    if (focused === this) {
      focused = null;
      hideExtractButton();
    }
    this.view?.destroy();
    this.view = null;
  }

  // A change makes a new view, because the node views depend on the value (see
  // liveNodeViews).
  setEditable(editable: boolean): void {
    if (editable === this.editable) return;
    this.editable = editable;
    this.view?.destroy();
    this.view = null;
    this.sync();
  }

  // Move the view into a new element, when the page shows the card again.
  remount(el: HTMLElement): void {
    this.el = el;
    if (this.view) el.appendChild(this.view.dom);
    else this.sync();
  }

  private updateExtract(): void {
    if (this.view) {
      updateExtractButton(this.view, () => this.dispatch({ type: "ExtractToCard", cardId: this.cardId }));
    }
  }

  private createView(state: EditorState): EditorView {
    const { dispatch, editable } = this;
    return new EditorView(this.el, {
      state,
      editable: () => editable,
      nodeViews: liveNodeViews(
        this.path,
        dispatch,
        editable,
        (cardId, el, path, sectionEditable) => new CardEditor(cardId, el, path, dispatch, sectionEditable),
        // A preview in a live editor is at depth 1.
        (el, content) => renderCardPreview(el, content, 1, dispatch)
      ),
      // Transactions are Msgs: forward them to the app's update() instead of
      // self-applying. update() applies the tr to the Model's EditorState and
      // syncEditors() projects the result back here.
      dispatchTransaction: (tr) => dispatch({ type: "EditorTx", cardId: this.cardId, tr }),
      handleDOMEvents: {
        focus: () => {
          focused = this;
          this.updateExtract();
          for (const listener of focusListeners) listener();
          return false;
        },
        blur: () => {
          if (focused === this) hideExtractButton();
          return false;
        },
      },
      // Cmd/Ctrl-click opens an external link; a plain click just places the
      // caret so the link text stays editable.
      handleClick(_view, _pos, event) {
        if (!(event.metaKey || event.ctrlKey)) return false;
        const a = (event.target as HTMLElement).closest("a.external-link") as HTMLAnchorElement | null;
        if (!a) return false;
        window.open(a.href, "_blank", "noopener");
        return true;
      },
      handleClickOn(view, _pos, node, nodePos, event, direct) {
        if (node.type.name === "card_link") {
          dispatch({ type: "NavigateTo", id: node.attrs["uuid"] as string });
          return true;
        }
        // Toggle a todo item only when the click lands on the checkbox marker —
        // i.e. left of the item's text — so clicking the text (or empty space
        // after it) places the caret instead of toggling. `direct` is true only
        // for the innermost clicked node.
        if (direct && node.type.name === "list_item") {
          const parent = view.state.doc.resolve(nodePos).parent;
          if (parent.type.name === "list" && parent.attrs["style"] === "todo") {
            const li = view.nodeDOM(nodePos) as HTMLElement | null;
            const text = li?.firstElementChild as HTMLElement | null;
            const onMarker = text != null && event.clientX < text.getBoundingClientRect().left;
            if (!onMarker) return false;
            const tr = view.state.tr.setNodeMarkup(nodePos, null, {
              ...node.attrs,
              checked: !(node.attrs["checked"] as boolean),
            });
            view.dispatch(tr);
            return true;
          }
        }
        return false;
      },
    });
  }
}

// Show the main editor of a card in `el`. A second call for the card moves the
// existing editor into the new element.
export function mountMainEditor(cardId: string, el: HTMLElement, dispatch: Dispatch): void {
  const existing = mainEditors.get(cardId);
  if (existing) existing.remount(el);
  else mainEditors.set(cardId, new CardEditor(cardId, el, [cardId], dispatch));
}

// Make each editor agree with the published Model. An editor that this loop
// makes gets its state in its constructor.
export function syncEditors(): void {
  for (const editor of editors) editor.sync();
}

export function destroyMainEditor(cardId: string): void {
  mainEditors.get(cardId)?.destroy();
  mainEditors.delete(cardId);
}

// The editor that the toolbar of a main card changes: the editor with the
// focus if it is in that card's page, else the main editor of the card.
export function toolbarEditor(mainCardId: string): CardEditor | undefined {
  const main = mainEditors.get(mainCardId);
  const mainDom = main?.view?.dom;
  if (focused?.view && mainDom?.contains(focused.view.dom)) return focused;
  return main;
}

export function onFocusChange(listener: () => void): void {
  focusListeners.add(listener);
}

// Returns the cards that the page shows in an editor now.
export function shownCardIds(): string[] {
  const ids = new Set<string>();
  for (const editor of editors) {
    if (editor.view?.dom.isConnected) ids.add(editor.cardId);
  }
  return [...ids];
}
